/**
 * Long explanations live behind an info icon, not a banner.
 *
 * Click opens a popover; hover still works via the aria-label. Keep one-line
 * captions next to the control — only the "how this works" paragraph belongs
 * here.
 */
import React, { useState } from 'react';
import { Box, IconButton, Popover, Typography } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';

export interface InfoHintProps {
  title?: string;
  children: React.ReactNode;
  /** Accessible name when title is omitted. */
  label?: string;
  size?: 'small' | 'medium';
}

const InfoHint: React.FC<InfoHintProps> = ({
  title,
  children,
  label,
  size = 'small',
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  const name = title || label || 'More information';

  return (
    <>
      <IconButton
        size={size}
        aria-label={name}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setAnchor(e.currentTarget);
        }}
        sx={{
          color: 'text.secondary',
          p: size === 'small' ? 0.4 : 0.75,
          '&:hover': { color: 'primary.main' },
        }}
      >
        <InfoOutlined fontSize="inherit" sx={{ fontSize: size === 'small' ? 18 : 22 }} />
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              maxWidth: 380,
              p: 1.75,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
            },
          },
        }}
      >
        {title && (
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
            {title}
          </Typography>
        )}
        <Box
          sx={{
            typography: 'body2',
            color: 'text.secondary',
            lineHeight: 1.55,
            '& p': { m: 0, mb: 1, '&:last-child': { mb: 0 } },
            '& ul': { m: 0, pl: 2.25 },
            '& li': { mb: 0.5 },
            '& strong': { color: 'text.primary', fontWeight: 600 },
          }}
        >
          {children}
        </Box>
      </Popover>
    </>
  );
};

export default InfoHint;
